// Renders a list of link candidates with per-row edge-type pickers.
//
// Phase 06 (`propose_links`) shipped the v1 selector — a tight list with
// rationale + radio. Phase 10b extended it with per-row edge-type radios.
// Phase 12 adds richer per-row metadata so the user can judge candidates
// without re-reading the issue: a retrieval-source badge (`search` /
// `backref`), one chip per matched term, one chip showing the backref
// occurrence count, and a per-row label / sublabel pair. For large
// candidate lists (≥ 10 rows) a search/filter input narrows the visible
// set.

import { useMemo } from "react";

import { uiProse } from "@loom/shared/brand";
import { useT } from "../../i18n/index.js";
import { useRemountSafeState } from "../../state/remount-safe-state";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";

const EDGE_TYPES = ["relates_to", "blocks", "blocked_by", "parent"] as const;
type EdgeType = (typeof EDGE_TYPES)[number];

const isEdgeType = (v: string): v is EdgeType =>
  (EDGE_TYPES as ReadonlyArray<string>).includes(v);

/** Search-or-backref reason annotation (mirrors `RetrievalReason` from
 *  `@loom/workflows/lib/discover-links`). */
interface RetrievalReason {
  readonly source: "search" | "backref" | string;
  readonly matchedTerm?: string;
  readonly occurrencesInBody?: number;
}

interface SelectorCandidate {
  readonly targetId: number;
  readonly edgeType: string;
  readonly rationale?: string;
  readonly retrievalReasons?: ReadonlyArray<RetrievalReason>;
  /** Phase 12: optional human-facing label / sublabel for the card. When
   *  absent, the card falls back to `#<targetId>` and the rationale. */
  readonly label?: string;
  readonly sublabel?: string;
}

interface SelectorBlockProps {
  readonly candidates: ReadonlyArray<SelectorCandidate>;
  readonly selectMode?: "checkbox" | "single";
  /**
   * When true, each row shows an edge-type radio group so the user can
   * override the default before confirming. Defaults to true for the v1
   * intake flow; legacy `propose_links` callers can pass false to hide it.
   */
  readonly editableEdgeType?: boolean;
  /**
   * Threshold above which the search/filter input is rendered. Defaults
   * to 10 — small lists don't need a filter, large lists do.
   */
  readonly filterThreshold?: number;
  /**
   * Remount-safe key (stable across the reconnect replay — a toolUseId or
   * proposalId) for the selection, edge overrides, filter and the one-shot
   * `confirmed` latch. Without it the wipe re-arms Confirm (a double-apply
   * hazard) and discards a selection built across many rows.
   */
  readonly stateKey?: string;
  readonly onConfirm: (selected: ReadonlyArray<SelectorCandidate>) => void;
}

const isSelectorCandidate = (v: unknown): v is SelectorCandidate => {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return typeof o.targetId === "number" && typeof o.edgeType === "string";
};

export const tryExtractCandidates = (output: unknown): ReadonlyArray<SelectorCandidate> | null => {
  if (typeof output !== "object" || output === null) return null;
  const maybe = (output as { candidates?: unknown }).candidates;
  if (!Array.isArray(maybe)) return null;
  const all = maybe.filter(isSelectorCandidate);
  return all.length > 0 ? all : null;
};

const matchesFilter = (c: SelectorCandidate, q: string): boolean => {
  if (q.length === 0) return true;
  const needle = q.toLowerCase();
  if (String(c.targetId).includes(needle)) return true;
  if (c.label?.toLowerCase().includes(needle)) return true;
  if (c.sublabel?.toLowerCase().includes(needle)) return true;
  if (c.rationale?.toLowerCase().includes(needle)) return true;
  if (c.retrievalReasons?.some((r) => r.matchedTerm?.toLowerCase().includes(needle)))
    return true;
  return false;
};

const sourceBadgeClass = (source: string): string => {
  if (source === "backref")
    return "border border-caution-line-strong bg-caution-soft text-caution-fg-2";
  if (source === "search")
    return "border border-line-strong bg-surface-2 text-ink-3";
  return "border border-line bg-surface text-ink-muted";
};

const primarySource = (
  reasons: ReadonlyArray<RetrievalReason> | undefined,
): string | null => {
  if (!reasons || reasons.length === 0) return null;
  // Backref is the stronger signal — surface it when present even if it
  // is not the first reason recorded.
  const back = reasons.find((r) => r.source === "backref");
  if (back) return "backref";
  return reasons[0]?.source ?? null;
};

const collectMatchedTerms = (
  reasons: ReadonlyArray<RetrievalReason> | undefined,
): string[] => {
  if (!reasons) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of reasons) {
    if (!r.matchedTerm) continue;
    if (seen.has(r.matchedTerm)) continue;
    seen.add(r.matchedTerm);
    out.push(r.matchedTerm);
  }
  return out;
};

const totalOccurrences = (
  reasons: ReadonlyArray<RetrievalReason> | undefined,
): number => {
  if (!reasons) return 0;
  return reasons.reduce(
    (n, r) => n + (typeof r.occurrencesInBody === "number" ? r.occurrencesInBody : 0),
    0,
  );
};

export function SelectorBlock({
  candidates,
  selectMode = "checkbox",
  editableEdgeType = true,
  filterThreshold = 10,
  stateKey,
  onConfirm,
}: SelectorBlockProps) {
  const t = useT();
  const sub = (piece: string): string | null =>
    stateKey !== undefined ? `${stateKey}:${piece}` : null;
  const [picked, setPicked] = useRemountSafeState<ReadonlySet<number>>(
    sub("picked"),
    () => new Set(),
  );
  const [edgeOverrides, setEdgeOverrides] = useRemountSafeState<
    ReadonlyMap<number, EdgeType>
  >(sub("edges"), () => new Map());
  const [confirmed, setConfirmed] = useRemountSafeState(sub("confirmed"), false);
  const [filter, setFilter] = useRemountSafeState(sub("filter"), "");

  const toggle = (targetId: number) => {
    const next = new Set(picked);
    if (selectMode === "single") {
      next.clear();
      next.add(targetId);
    } else if (next.has(targetId)) {
      next.delete(targetId);
    } else {
      next.add(targetId);
    }
    setPicked(next);
  };

  const setEdge = (targetId: number, edge: EdgeType) => {
    const next = new Map(edgeOverrides);
    next.set(targetId, edge);
    setEdgeOverrides(next);
  };

  const resolveEdge = (c: SelectorCandidate): string => {
    const override = edgeOverrides.get(c.targetId);
    if (override !== undefined) return override;
    return c.edgeType;
  };

  const showFilter = candidates.length >= filterThreshold;
  const visibleCandidates = useMemo(
    () => (showFilter ? candidates.filter((c) => matchesFilter(c, filter)) : candidates),
    [candidates, showFilter, filter],
  );

  const ordered = useMemo(
    () =>
      candidates
        .filter((c) => picked.has(c.targetId))
        .map((c) => ({ ...c, edgeType: resolveEdge(c) })),
    [candidates, picked, edgeOverrides],
  );

  if (confirmed) {
    return (
      <Card className="p-3">
        <div className="text-xs text-ink-muted">
          Selected {ordered.length} link
          {ordered.length === 1 ? "" : "s"}.
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">
          {t("chatRender.linkCandidates")}
        </div>
        <div className="text-[10px] text-ink-faint">
          {visibleCandidates.length} / {candidates.length}
        </div>
      </div>
      {showFilter && (
        <input
          type="text"
          placeholder={t("chatRender.filterCandidates")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="mb-2 w-full rounded-md border border-line px-2 py-1 text-xs"
          data-testid="selector-filter"
        />
      )}
      <ul className="mb-3 space-y-2">
        {visibleCandidates.map((c) => {
          const id = `cand-${c.targetId}`;
          const checked = picked.has(c.targetId);
          const currentEdge = resolveEdge(c);
          const source = primarySource(c.retrievalReasons);
          const terms = collectMatchedTerms(c.retrievalReasons);
          const occ = totalOccurrences(c.retrievalReasons);
          return (
            <li key={c.targetId}>
              <label
                htmlFor={id}
                className="flex cursor-pointer items-start gap-2 rounded-md border border-line p-2 hover:bg-surface-2"
              >
                <input
                  id={id}
                  type={selectMode === "single" ? "radio" : "checkbox"}
                  name="selector"
                  className="mt-0.5"
                  checked={checked}
                  onChange={() => toggle(c.targetId)}
                />
                <span className="flex-1 text-xs">
                  <span className="flex flex-wrap items-center gap-1">
                    <span className="font-mono text-ink-3">
                      #{c.targetId}
                    </span>
                    {c.label !== undefined && (
                      <span className="font-medium text-ink-2">
                        {c.label}
                      </span>
                    )}
                    <span className="text-ink-subtle">({currentEdge})</span>
                    {source !== null && (
                      <span
                        className={`rounded-sm px-1 py-[1px] text-[9px] uppercase tracking-wider ${sourceBadgeClass(source)}`}
                        data-testid={`source-${c.targetId}`}
                      >
                        {source}
                      </span>
                    )}
                  </span>
                  {c.sublabel !== undefined && (
                    <span className="block text-[11px] text-ink-subtle">
                      {c.sublabel}
                    </span>
                  )}
                  {c.rationale !== undefined && (
                    <span className="block text-ink-3">{uiProse(c.rationale)}</span>
                  )}
                  {(terms.length > 0 || occ > 0) && (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {terms.map((t) => (
                        <span
                          key={`term-${t}`}
                          className="rounded-sm border border-line bg-surface-2 px-1 py-[1px] text-[10px] text-ink-3"
                          data-testid={`term-chip-${c.targetId}`}
                        >
                          “{t}”
                        </span>
                      ))}
                      {occ > 0 && (
                        <span
                          className="rounded-sm border border-caution-line bg-caution-soft px-1 py-[1px] text-[10px] text-caution-fg-2"
                          data-testid={`occ-chip-${c.targetId}`}
                        >
                          #-ref ×{occ}
                        </span>
                      )}
                    </span>
                  )}
                  {editableEdgeType && checked && (
                    <span className="mt-1 flex flex-wrap gap-2">
                      {EDGE_TYPES.map((edge) => {
                        const radioId = `cand-${c.targetId}-edge-${edge}`;
                        return (
                          <span
                            key={edge}
                            className="inline-flex items-center gap-1"
                          >
                            <input
                              id={radioId}
                              type="radio"
                              name={`edge-${c.targetId}`}
                              className="h-3 w-3"
                              checked={currentEdge === edge}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (isEdgeType(edge)) setEdge(c.targetId, edge);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <label
                              htmlFor={radioId}
                              className="text-[11px] text-ink-muted"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {edge}
                            </label>
                          </span>
                        );
                      })}
                    </span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <Button
        onClick={() => {
          setConfirmed(true);
          onConfirm(ordered);
        }}
        disabled={ordered.length === 0}
      >
        {t("chatRender.confirmSelection")}
      </Button>
    </Card>
  );
}

export type { SelectorCandidate };
